/**
 * useBoardTaskLayout - derived layout calculations for the board
 * Extracted from BoardV3.tsx for readability
 */

import { useMemo } from 'react';
import type { Task } from '../../types';
import type { ScheduledTask } from '../scheduler';
import { DAYS_PER_WEEK, getMaxRow } from '../utils/boardHelpers';
import { getTaskDayIndex, businessDayIndexToDate, getKwFromDate } from '../../lib/dateUtils';

export interface TaskLayoutResult {
  totalDays: number;
  tasksByColumn: Record<string, Task[]>;
  rowAssignments: Record<string, Map<string, number>>;
  columnYOffsets: Record<string, number>;
  totalHeight: number;
  visibleWeeks: { kw: number; year: number; startDay: number; weekIndex: number }[];
}

export function useBoardTaskLayout(
  tasks: Task[],
  columns: string[],
  scheduledTasks: Map<string, ScheduledTask>,
  scrollPos: { x: number },
  dayWidth: number,
  rowHeight: number,
  columnLabelWidth: number,
  boardStartDate?: string,
): TaskLayoutResult {

  const minDaysForTasks = useMemo(() => {
    let maxEndDay = 52 * DAYS_PER_WEEK;
    for (const task of tasks) {
      const scheduled = scheduledTasks.get(task.id);
      if (scheduled && scheduled.endDay > maxEndDay) {
        maxEndDay = scheduled.endDay;
      }
    }
    return maxEndDay + 10 * DAYS_PER_WEEK;
  }, [tasks, scheduledTasks]);

  const totalDays = useMemo(() => {
    const visibleEndDay = Math.ceil((scrollPos.x + 2000) / dayWidth) + 20 * DAYS_PER_WEEK;
    return Math.max(minDaysForTasks, visibleEndDay);
  }, [scrollPos.x, dayWidth, minDaysForTasks]);

  const tasksByColumn = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    for (const col of columns) {
      grouped[col] = [];
    }
    for (const task of tasks) {
      if (grouped[task.column]) {
        grouped[task.column].push(task);
      }
    }
    return grouped;
  }, [tasks, columns]);

  const rowAssignments = useMemo(() => {
    const assignments: Record<string, Map<string, number>> = {};

    for (const column of columns) {
      const colTasks = tasksByColumn[column] || [];
      const colAssignments = new Map<string, number>();
      const rowEnds: number[] = [];

      const sorted = [...colTasks].sort((a, b) => getTaskDayIndex(a.startDate, boardStartDate) - getTaskDayIndex(b.startDate, boardStartDate));

      for (const task of sorted) {
        const duration = scheduledTasks.get(task.id)?.effectiveDays ?? 10;
        const taskStartDay = getTaskDayIndex(task.startDate, boardStartDate);
        const taskEnd = taskStartDay + duration;

        let assignedRow = -1;
        for (let row = 0; row < rowEnds.length; row++) {
          if (rowEnds[row] <= taskStartDay) {
            assignedRow = row;
            rowEnds[row] = taskEnd;
            break;
          }
        }

        if (assignedRow === -1) {
          assignedRow = rowEnds.length;
          rowEnds.push(taskEnd);
        }

        colAssignments.set(task.id, assignedRow);
      }

      assignments[column] = colAssignments;
    }

    return assignments;
  }, [tasksByColumn, columns, scheduledTasks]);

  const columnYOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    let currentY = 0;

    for (const column of columns) {
      offsets[column] = currentY;
      const rowCount = Math.max(1, getMaxRow(rowAssignments[column]) + 1);
      currentY += rowCount * rowHeight + 8;
    }

    return offsets;
  }, [columns, rowAssignments, rowHeight]);

  const totalHeight = useMemo(() => {
    let height = 0;
    for (const column of columns) {
      const rowCount = Math.max(1, getMaxRow(rowAssignments[column]) + 1);
      height += rowCount * rowHeight + 8;
    }
    return Math.max(height, 400);
  }, [columns, rowAssignments, rowHeight]);

  const visibleWeeks = useMemo(() => {
    const startDayCalc = Math.max(0, Math.floor((scrollPos.x - columnLabelWidth) / dayWidth) - 5);
    const endDayCalc = Math.ceil((scrollPos.x + 1400) / dayWidth) + 10;

    const startWeekIdx = Math.floor(startDayCalc / DAYS_PER_WEEK);
    const endWeekIdx = Math.ceil(endDayCalc / DAYS_PER_WEEK);

    const weeks: { kw: number; year: number; startDay: number; weekIndex: number }[] = [];
    for (let w = startWeekIdx; w <= endWeekIdx; w++) {
      const weekStartDate = businessDayIndexToDate(w * DAYS_PER_WEEK, boardStartDate);
      const { kw, year } = getKwFromDate(weekStartDate);
      weeks.push({ kw, year, startDay: w * DAYS_PER_WEEK, weekIndex: w });
    }
    return weeks;
  }, [scrollPos.x, dayWidth, columnLabelWidth]);

  return {
    totalDays,
    tasksByColumn,
    rowAssignments,
    columnYOffsets,
    totalHeight,
    visibleWeeks,
  };
}
